import {
  createNote,
  GeneratedNoteGroup,
  getNoteTicks,
  Note,
  NoteType,
} from './note-definition';
import { TimeSignature, TimeSignatureComplexity } from './time-signature';
import { Measure } from './vex';

// A note may not obscure the middle of the measure: one that starts before it
// and ends after it is written as two notes joined by a tie (Haltebogen)
// instead. Notes that cross a weaker beat are left alone, so syncopations such
// as 8th-quarter-8th keep their conventional single-note spelling.

// Durations are held as integer ticks (1 tick = a 16th note) to keep the
// arithmetic free of floating point drift.
const SIMPLE_TICKS_PER_BEAT = 4; // a quarter note
const COMPOUND_TICKS_PER_BEAT = 6; // a dotted quarter note

// The durations a single note head can express, largest first.
const notatableDurations: {
  ticks: number;
  type: NoteType;
  dotted: boolean;
}[] = [
  { ticks: 24, type: NoteType.W, dotted: true },
  { ticks: 16, type: NoteType.W, dotted: false },
  { ticks: 12, type: NoteType.H, dotted: true },
  { ticks: 8, type: NoteType.H, dotted: false },
  { ticks: 6, type: NoteType.Q, dotted: true },
  { ticks: 4, type: NoteType.Q, dotted: false },
  { ticks: 3, type: NoteType.E, dotted: true },
  { ticks: 2, type: NoteType.E, dotted: false },
  { ticks: 1, type: NoteType.S, dotted: false },
];

// How long a beat is. `beatsPerMeasure` counts quarter notes in a simple or
// alla breve meter, and dotted quarters in a compound one.
export const getTicksPerBeat = (timeSignature: TimeSignature): number =>
  timeSignature.complexity === TimeSignatureComplexity.COMPOUND
    ? COMPOUND_TICKS_PER_BEAT
    : SIMPLE_TICKS_PER_BEAT;

const getMeasureTicks = (timeSignature: TimeSignature): number =>
  timeSignature.beatsPerMeasure * getTicksPerBeat(timeSignature);

// The number of beats as they are written in the time signature. For alla
// breve, `beatsPerMeasure` counts quarter notes while the meter is felt in
// half notes, so 2/2 reads as four but is written as two.
const getWrittenBeats = (timeSignature: TimeSignature): number =>
  timeSignature.complexity === TimeSignatureComplexity.ALLA_BREVE
    ? timeSignature.beatsPerMeasure / 2
    : timeSignature.beatsPerMeasure;

// The tick offset of the middle of the measure, or null for meters that divide
// into unequal halves (3/4, 5/4, 7/4, 3/2, 9/8) and so have no middle a note
// could obscure.
export const getMeasureMiddleTicks = (
  timeSignature: TimeSignature
): number | null =>
  getWrittenBeats(timeSignature) % 2 === 0
    ? getMeasureTicks(timeSignature) / 2
    : null;

// Not every note that spans the middle actually hides it. Convention still
// reads two cases clearly:
//   - a note starting at the barline, which is how a whole note or a dotted
//     half followed by a quarter is written;
//   - an undotted value starting on a beat, the traditional quarter-half-
//     quarter of 4/4.
// A dotted value or an off-beat start hides the middle and gets tied instead.
// Rests are held to the stricter rule and get neither exception: a half rest
// on beat 2 of 4/4 is written as two quarter rests.
const maySpanMiddle = (
  note: Note,
  startTicks: number,
  ticksPerBeat: number
): boolean => {
  if (note.rest) {
    return false;
  }

  return (
    startTicks === 0 || (!note.dotted && startTicks % ticksPerBeat === 0)
  );
};

// Splits a duration into the note values needed to write it, largest first.
// Every duration is reachable because a 16th covers a single tick.
const decompose = (ticks: number): { type: NoteType; dotted: boolean }[] => {
  const parts: { type: NoteType; dotted: boolean }[] = [];
  let remaining = ticks;

  notatableDurations.forEach((duration) => {
    while (remaining >= duration.ticks) {
      parts.push({ type: duration.type, dotted: duration.dotted });
      remaining -= duration.ticks;
    }
  });

  return parts;
};

// Rewrites one note as the notes needed to write it around the middle. Both
// sides may need more than one note value. Notes are tied to each other so
// they sound as one; rests just sit next to each other.
const splitNote = (
  note: Note,
  beforeMiddle: number,
  afterMiddle: number
): Note[] => {
  const parts = [...decompose(beforeMiddle), ...decompose(afterMiddle)];

  return parts.map((part, index) => ({
    ...createNote(part.type, note.rest, part.dotted),
    ...(!note.rest && index < parts.length - 1 && { tieStart: true }),
    ...(!note.rest && index > 0 && { tieStop: true }),
  }));
};

const isBeamable = (note: Note): boolean =>
  !note.rest && (note.type === NoteType.E || note.type === NoteType.S);

// Works out which runs of notes carry a beam. Flagged notes are beamed
// together for as long as they stay inside the same beat, which is what makes
// the beat visible; a longer value breaks a run. A beam carries on over a rest
// that has notes on both sides of it, but never begins or ends on one, so a
// run is trimmed back to a note at either end.
//
// This runs only for groups a split has rewritten, whose hand-written beam
// ranges no longer line up with their notes anyway.
const computeBeams = (
  notes: Note[],
  startTicks: number,
  ticksPerBeat: number
): string[] => {
  const ranges: string[] = [];
  let cursor = startTicks;
  let run: { index: number; beamable: boolean }[] = [];
  let runBeat = -1;

  const endRun = () => {
    let first = 0;
    let last = run.length - 1;

    while (first <= last && !run[first].beamable) {
      first++;
    }

    while (last >= first && !run[last].beamable) {
      last--;
    }

    const beamed = run.slice(first, last + 1);

    // A beam needs at least two notes; a lone 8th keeps its flag.
    if (beamed.filter((item) => item.beamable).length >= 2) {
      ranges.push(`${beamed[0].index}-${beamed[beamed.length - 1].index}`);
    }

    run = [];
  };

  notes.forEach((note, index) => {
    const beat = Math.floor(cursor / ticksPerBeat);

    if (!isBeamable(note) && !note.rest) {
      endRun();
      runBeat = -1;
    } else {
      if (beat !== runBeat) {
        endRun();
        runBeat = beat;
      }

      run.push({ index, beamable: isBeamable(note) });
    }

    cursor += getNoteTicks(note);
  });

  endRun();

  return ranges;
};

const splitNoteGroup = (
  noteGroup: GeneratedNoteGroup,
  startTicks: number,
  middleTicks: number,
  measureTicks: number,
  ticksPerBeat: number
): GeneratedNoteGroup => {
  // Tuplet notes do not sit on the binary tick grid.
  if (noteGroup.tuplet) {
    return noteGroup;
  }

  const notes: Note[] = [];
  let cursor = startTicks;
  let changed = false;

  noteGroup.notes.forEach((note) => {
    const ticks = getNoteTicks(note);
    const spansMiddle = cursor < middleTicks && cursor + ticks > middleTicks;

    // A rest covering the whole measure is the measure's rest, whatever it is
    // written as: a whole rest, or the half rest that fills a 2/4 bar.
    const fillsMeasure = cursor === 0 && cursor + ticks >= measureTicks;

    if (
      spansMiddle &&
      !fillsMeasure &&
      !maySpanMiddle(note, cursor, ticksPerBeat)
    ) {
      notes.push(
        ...splitNote(note, middleTicks - cursor, cursor + ticks - middleTicks)
      );
      changed = true;
    } else {
      notes.push(note);
    }

    cursor += ticks;
  });

  if (!changed) {
    return noteGroup;
  }

  const beams = computeBeams(notes, startTicks, ticksPerBeat);

  return { ...noteGroup, notes, beam: beams.length ? beams : undefined };
};

// Rewrites every note that crosses the middle of the measure as two tied
// notes, and every rest that crosses it as two rests. Running it again on the
// result changes nothing, since nothing crosses the middle any more.
export const splitNotesAcrossMiddle = (
  measure: Measure,
  timeSignature: TimeSignature
): Measure => {
  const middleTicks = getMeasureMiddleTicks(timeSignature);

  if (middleTicks === null) {
    return measure;
  }

  const measureTicks = getMeasureTicks(timeSignature);
  const ticksPerBeat = getTicksPerBeat(timeSignature);
  let cursor = 0;

  const noteGroups = measure.noteGroups.map((noteGroup) => {
    const groupStart = cursor;

    // The group's own duration is authoritative, because a note's written
    // value can differ from the time it takes up: a whole rest fills whatever
    // the measure is long.
    cursor += noteGroup.duration * ticksPerBeat;

    return splitNoteGroup(
      noteGroup,
      groupStart,
      middleTicks,
      measureTicks,
      ticksPerBeat
    );
  });

  return { ...measure, noteGroups };
};

export const splitMeasuresAcrossMiddle = (
  measures: Measure[],
  timeSignature: TimeSignature
): Measure[] =>
  measures.map((measure) => splitNotesAcrossMiddle(measure, timeSignature));

// Maps each playback index onto the note head that should light up for it.
// Notes, rendered note heads and playback patterns stay one-to-one, so the
// mapping is the identity everywhere except at a tie continuation: that points
// back at the note head actually struck, keeping the highlight on it for the
// whole sustained note rather than jumping to the second head halfway through.
export const getHighlightNoteHeadIndexes = (measures: Measure[]): number[] => {
  const indexes: number[] = [];

  measures.forEach((measure) => {
    measure.noteGroups.forEach((noteGroup) => {
      noteGroup.notes.forEach((note) => {
        const index = indexes.length;

        indexes.push(note.tieStop && index > 0 ? indexes[index - 1] : index);
      });
    });
  });

  return indexes;
};
